"use client";
import { Box, Autocomplete, TextField, ListItem, Accordion } from "@mui/material";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { SpaceMd, ColorTextMedium, SpaceXs, SpaceXxs, ColorWhite, ColorTextLink, ColorTextError, RadiusMd, SpaceSm, FontWeightDefault, FontLineHeightMd, ColorTextDefault, SizeBorderWidthThin, ColorTextLight, ColorBlue100, SpaceLg } from "@nxt-costco-com/forge-design-tokens";
import { ChevronDown } from "@nxt-costco-com/forge-icons/icons/ChevronDown";
const StyledHeader = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    gap: ${SpaceMd};
`;
const StyledProductHeader = styled(Box)`
    display: flex;
    align-items: flex-start;
    align-self: stretch;
    gap: ${SpaceMd};
`;
const ProductImageSx = {
  alignItems: "center",
  border: "1px solid ColorGray500",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  width: "72px"
};
const ProductDecriptionTextSx = {
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 3,
  display: "-webkit-box",
  flex: "1 0 0",
  overflow: "hidden",
  textOverflow: "ellipsis"
};
const StyledForm = styled("form")`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    gap: ${SpaceMd};
`;
const StyledAutoComplete = styled(Autocomplete)`
    width: 100%;
    & .MuiOutlinedInput-root.MuiInputBase-sizeSmall {
        padding: 0px;
        &. MuiAutocomplete-input {
            padding-right: ${SpaceMd};
        }
    }
    & .MuiAutocomplete-inputFocused {
        box-sizing: border-box;
        height: 40px;
    }
`;
const StyledTextField = styled(TextField)`
    width: 100%;
    height: auto;

    label {
        background: none;
        color: ${ColorTextMedium};
        max-width: 185px;
    }

    label.MuiFormLabel-filled {
        padding-right: ${SpaceXs};
        padding-left: ${SpaceXxs};
        margin-left: 0px;
        margin-right: 0px;
        background: ${ColorWhite};
    }

    label.Mui-focused {
        background: ${ColorWhite};
        color: ${ColorTextLink};
        margin-left: 0px;
        padding-right: ${SpaceXs};
        padding-left: ${SpaceXxs};
    }

    label.Mui-error {
        color: ${ColorTextError};
    }

    & .MuiOutlinedInput-root {
        background-color: ${ColorWhite};
        gap: 0px;
        border-radius: ${RadiusMd};
        padding-right: ${SpaceMd};
        padding-left: 20px;

        .MuiInputAdornment-positionEnd {
            button {
                margin-right: -${SpaceSm};
            }
        }

        input {
            padding-right: ${SpaceMd};
            padding-left: 0px;
            font-weight: ${FontWeightDefault};
            line-height: ${FontLineHeightMd};
            word-wrap: break-word;
            color: ${ColorTextDefault};
        }

        button {
            border-top-left-radius: 0px;
            border-bottom-left-radius: 0px;
        }
    }

    & .MuiOutlinedInput-root.Mui-focused > .MuiOutlinedInput-notchedOutline {
        border-color: ${ColorTextLink};
    }

    & .MuiOutlinedInput-root.Mui-error > .MuiOutlinedInput-notchedOutline {
        border-color: ${ColorTextError};
        border-width: 2px;
    }

    &
        .MuiOutlinedInput-root.Mui-focused.Mui-error
        > .MuiOutlinedInput-notchedOutline {
        border-color: ${ColorTextError};
        border-width: 2px;
    }

    & .MuiInputAdornment-positionStart {
        padding-right: ${SpaceXs};
        margin-right: 0px;
    }

    & .MuiInputAdornment-positionEnd {
        padding-left: ${SpaceXs};
        margin-left: 0px;
    }

    & .MuiInputBase-multiline {
        width: 100%;
        height: auto;
    }

    & .MuiOutlinedInput-notchedOutline {
        border-width: ${SizeBorderWidthThin};
        border-color: ${ColorTextLight};

        legend {
            padding-right: 0px;
            padding-left: 0px;

            & span {
                padding-right: 0px;
                padding-left: 0px;
            }
        }
    }

    & .MuiFormHelperText-root {
        margin-right: ${SpaceSm};
        margin-left: ${SpaceSm};
    }
`;
const StyledLocationSuggestionListItem = styled(ListItem)`
    &.MuiAutocomplete-option.Mui-focused {
        background-color: ${ColorBlue100};
    }
`;
const StyledAccordion = styled(Accordion)`
    &::before {
        display: none;
    }
    background-color: ${ColorWhite};
    border: none;
    box-shadow: none;
`;
const AccordianSummarySx = {
  "& .MuiAccordionSummary-content": {
    margin: 0
  },
  "&.MuiButtonBase-root.MuiAccordionSummary-root.Mui-focusVisible": {
    backgroundColor: "transparent",
    outline: "2px auto -webkit-focus-ring-color",
    outlineoffset: "2px"
  },
  alignItems: "center",
  display: "flex",
  gap: SpaceXxs,
  minHeight: "18px",
  padding: 0
};
const StyledChevronDown = styled(ChevronDown)`
    color: ${ColorTextDefault};
    height: 18px;
    width: 18px;
`;
const StyledNotifcationWrapper = styled(Box)`
    background-color: white;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    overflow: auto;
    padding: ${SpaceMd};
    height: 100%;
`;
const StyledHiddenLegend = styled("legend")(() => ({
  ...visuallyHidden
}));
const StyledSpinnerWrapper = styled(Box)`
    display: flex;
    justify-content: center;
    align-items: flex-start;
    align-self: stretch;
    padding-top: ${SpaceLg};
`;
export {
  AccordianSummarySx,
  ProductDecriptionTextSx,
  ProductImageSx,
  StyledAccordion,
  StyledAutoComplete,
  StyledChevronDown,
  StyledForm,
  StyledHeader,
  StyledHiddenLegend,
  StyledLocationSuggestionListItem,
  StyledNotifcationWrapper,
  StyledProductHeader,
  StyledSpinnerWrapper,
  StyledTextField
};
